import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getSiteCategories, createMyListing, getPublishLimit, PublishLimitInfo } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import request from "@/utils/api";
import { getSiteSettings } from "@/utils/adminApi";

// 获取当前站点ID
const getSiteId = () => localStorage.getItem('current_site_id') || '';

export default function PublishPage() {
  const navigate = useNavigate();
  const { isLoggedIn, isLoading: authLoading } = useAuth();
  const [categories, setCategories] = useState<{ id: string; name: string; icon: string }[]>([]);
  const [publishLimit, setPublishLimit] = useState<PublishLimitInfo | null>(null);
  const [loadingLimit, setLoadingLimit] = useState(true);
  const [enableVip, setEnableVip] = useState(true);

  // 检查登录状态
  useEffect(() => {
    if (!authLoading && !isLoggedIn) {
      navigate("/login", { state: { from: "/publish" } });
    }
  }, [isLoggedIn, authLoading, navigate]);

  // 加载分类和发布限制
  useEffect(() => {
    if (!isLoggedIn) return;

    Promise.all([
      getSiteCategories(),
      getPublishLimit().catch(() => null),
      getSiteSettings().catch(() => null),
    ]).then(([cats, limit, siteSettings]) => {
      setCategories(cats.filter((c) => !c.parent_id));
      setPublishLimit(limit);
      if (siteSettings) {
        setEnableVip(siteSettings.enable_vip !== false);
      }
      setLoadingLimit(false);
    });
  }, [isLoggedIn]);

  if (authLoading || !isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="pt-20">
        <PublishForm
          categories={categories}
          publishLimit={publishLimit}
          loadingLimit={loadingLimit}
          enableVip={enableVip}
        />
      </main>
      <Footer />
    </div>
  );
}

interface PublishFormProps {
  categories: { id: string; name: string; icon: string }[];
  publishLimit: PublishLimitInfo | null;
  loadingLimit: boolean;
  enableVip: boolean;
}

function PublishForm({ categories, publishLimit, loadingLimit, enableVip }: PublishFormProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    categoryId: "",
    title: "",
    content: "",
    companyName: "",
    contactName: "",
    contactPhone: "",
    contactWechat: "",
  });
  const [images, setImages] = useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // 下载远程图片（通过后端代理）
  const downloadImage = async (url: string): Promise<string> => {
    try {
      setUploadProgress(`正在下载图片...`);
      const result = await request<{ url: string }>('/api/sites/' + getSiteId() + '/fetch-image', {
        params: { url },
      });
      if (result && (result as any).url) {
        return (result as any).url;
      }
      return url;
    } catch (err) {
      console.error("下载图片失败:", err);
      return url;
    }
  };

  // 上传图片到服务器
  const uploadImage = async (base64Data: string): Promise<string> => {
    try {
      const response = await fetch(base64Data);
      const blob = await response.blob();
      const ext = blob.type.split('/')[1] || 'png';
      const filename = `image_${Date.now()}.${ext}`;

      const formData = new FormData();
      formData.append('file', blob, filename);

      const result = await request<{ url: string }>('/upload', {
        method: 'POST',
        body: formData as any,
      });

      if (result && (result as any).url) {
        return (result as any).url;
      }
      return base64Data;
    } catch (err) {
      console.error("图片上传失败:", err);
      return base64Data;
    }
  };

  // 处理粘贴事件
  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // 检查是否有图片
    const items = clipboardData.items;
    let hasImages = false;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        hasImages = true;
        break;
      }
    }

    if (hasImages) {
      e.preventDefault();
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          if (images.length >= 6) {
            setError("最多上传6张图片");
            setTimeout(() => setError(""), 3000);
            break;
          }
          const file = items[i].getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              const base64 = event.target?.result as string;
              setImages((prev) => [...prev, base64]);
            };
            reader.readAsDataURL(file);
          }
        }
      }
      return;
    }

    // 检查 HTML 内容中的图片（图文一起复制）
    const html = clipboardData.getData("text/html");
    if (html) {
      const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
      const matches = [...html.matchAll(imgRegex)];

      if (matches.length > 0) {
        e.preventDefault();

        let plainText = clipboardData.getData("text/plain");

        // 过滤掉图片文件名
        const lines = plainText.split('\n');
        const filteredLines = lines.filter(line => {
          const trimmed = line.trim();
          if (/^[\w\-]+\.(png|jpg|jpeg|gif|webp|bmp)(\s*\.png)?$/i.test(trimmed)) {
            return false;
          }
          if (trimmed === '') return false;
          return true;
        });
        plainText = filteredLines.join('\n');

        const imageUrls = matches.map((m) => m[1]).filter((url) => url.startsWith("http"));

        if (images.length + imageUrls.length > 6) {
          setError(`最多上传6张图片`);
          setTimeout(() => setError(""), 3000);
          setFormData((prev) => ({
            ...prev,
            content: prev.content + plainText,
          }));
          return;
        }

        // 下载图片
        for (const url of imageUrls) {
          const downloadedImage = await downloadImage(url);
          setImages((prev) => [...prev, downloadedImage]);
        }

        // 粘贴文字
        setFormData((prev) => ({
          ...prev,
          content: prev.content + plainText,
        }));
      }
    }
  }, [images]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;
      Array.from(files).forEach(file => {
        if (images.length >= 6) {
          setError("最多上传6张图片");
          setTimeout(() => setError(""), 3000);
          return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target?.result as string;
          setImages((prev) => [...prev, base64]);
        };
        reader.readAsDataURL(file);
      });
    };
    input.click();
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.categoryId) {
      setError("请选择分类");
      return;
    }
    if (!formData.title.trim()) {
      setError("请输入标题");
      return;
    }
    if (!formData.contactName.trim()) {
      setError("请输入联系人姓名");
      return;
    }
    if (!formData.contactPhone.trim()) {
      setError("请输入联系电话");
      return;
    }
    if (!formData.content.trim()) {
      setError("请输入详情内容");
      return;
    }

    // 检查发布限制
    if (publishLimit && !publishLimit.can_publish) {
      const chargeAmount = publishLimit.charge_per_extra ?? 1;
      setError(`您本月的发布次数已用完，超出部分将收取 ${chargeAmount} 元/条`);
      return;
    }

    setIsSubmitting(true);
    setUploadProgress("正在上传图片...");

    try {
      // 上传 base64 图片
      const finalImages: string[] = [];
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        setUploadProgress(`正在上传图片 ${i + 1}/${images.length}...`);
        if (img.startsWith('data:')) {
          const uploadedUrl = await uploadImage(img);
          finalImages.push(uploadedUrl);
        } else {
          finalImages.push(img);
        }
      }

      setUploadProgress("正在保存信息...");

      await createMyListing({
        category_id: formData.categoryId,
        title: formData.title,
        content: formData.content,
        images: finalImages.length > 0 ? finalImages : undefined,
        company_name: formData.companyName || undefined,
        contact_name: formData.contactName || undefined,
        contact_phone: formData.contactPhone,
        contact_wechat: formData.contactWechat || undefined,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate("/dashboard/my-listings");
      }, 2000);
    } catch (err: any) {
      setError(err.message || "发布失败，请重试");
    } finally {
      setIsSubmitting(false);
      setUploadProgress("");
    }
  };

  if (success) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <div className="max-w-md mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-green-50">
            <i className="ri-check-line text-3xl text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">发布成功！</h2>
          <p className="text-gray-500 text-sm mb-6">您的服务信息已成功发布，正在跳转我的发布页面...</p>
          <button
            onClick={() => navigate("/dashboard/my-listings")}
            className="px-6 py-2.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors text-sm"
          >
            查看我的发布
          </button>
        </div>
      </div>
    );
  }

  const isVip = publishLimit?.is_vip;
  const remainingFree = publishLimit?.remaining_free ?? 0;
  const freeLimit = publishLimit?.free_limit ?? 10;
  const chargePerExtra = publishLimit?.charge_per_extra ?? 1;
  const publishedCount = publishLimit?.published_count ?? 0;

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">发布服务信息</h1>
          <p className="text-sm text-gray-500 mt-1">填写以下信息，让更多客户发现您的服务</p>
        </div>

        {/* 发布限制提示 */}
        {!loadingLimit && enableVip && (
          <div className={`mb-6 p-4 rounded-xl ${isVip ? 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200' : 'bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200'}`}>
            {isVip ? (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-vip-diamond-fill text-white"></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-purple-700">尊贵的VIP会员</span>
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full">有效期内无限发布</span>
                  </div>
                  <p className="text-sm text-purple-600">
                    VIP有效期至：{publishLimit?.vip_expires_at ? new Date(publishLimit.vip_expires_at).toLocaleDateString('zh-CN') : '未知'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-medal-line text-white"></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-orange-700">普通会员发布额度</span>
                    <span className="text-sm text-orange-600">
                      已发布 <span className="font-bold">{publishedCount}</span> / {freeLimit} 条
                    </span>
                  </div>
                  <div className="w-full bg-orange-100 rounded-full h-2 mb-2">
                    <div
                      className="bg-gradient-to-r from-orange-500 to-yellow-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (publishedCount / freeLimit) * 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-orange-600">
                      本月剩余 <span className="font-bold text-orange-700">{remainingFree}</span> 条发布额度
                    </p>
                    {remainingFree === 0 && (
                      <span className="px-3 py-1 bg-orange-500 text-white text-xs rounded-full">
                        超出部分 {chargePerExtra} 元/条
                      </span>
                    )}
                  </div>
                  {remainingFree === 0 && (
                    <p className="text-xs text-orange-500 mt-1">
                      温馨提示：超出额度后将按每条 {chargePerExtra} 元收费，建议升级VIP享受无限发布
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* 左右两栏布局 */}
          <div className="flex flex-col lg:flex-row">
            {/* 左侧 - 基本信息 */}
            <div className="flex-1 p-5 md:p-8 border-b lg:border-b-0 lg:border-r border-gray-100">
              <h3 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <i className="ri-file-text-line text-orange-500"></i>
                基本信息
              </h3>

              {/* 分类标签选择（单选） */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  选择分类 <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, categoryId: cat.id }))}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                        formData.categoryId === cat.id
                          ? 'bg-orange-500 text-white shadow-md shadow-orange-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {cat.icon && <i className={`${cat.icon} mr-1`}></i>}
                      {cat.name}
                    </button>
                  ))}
                </div>
                {formData.categoryId && (
                  <p className="text-xs text-green-600 mt-2">
                    <i className="ri-check-line mr-1"></i>已选择
                  </p>
                )}
              </div>

              {/* 标题 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  信息标题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="例如：专业企业官网定制开发"
                  maxLength={100}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.title.length}/100</p>
              </div>

              {/* 联系信息 */}
              <div className="mb-5">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">
                  <i className="ri-contacts-line text-gray-400"></i>
                  联系方式
                </h4>
                <div className="space-y-3">
                  <div className="relative">
                    <i className="ri-building-2-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="请输入公司名称（选填）"
                      maxLength={100}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                  <div className="relative">
                    <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      placeholder="请输入联系人姓名"
                      maxLength={50}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                  <div className="relative">
                    <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleChange}
                      placeholder="请输入联系电话"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                  <div className="relative">
                    <i className="ri-wechat-fill absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="contactWechat"
                      value={formData.contactWechat}
                      onChange={handleChange}
                      placeholder="请输入微信号（选填）"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 右侧 - 详情和图片 */}
            <div className="flex-1 p-5 md:p-8 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <i className="ri-image-fill text-orange-500"></i>
                详情与图片
              </h3>

              {/* 图片上传 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  上传图片 <span className="text-gray-400 font-normal">（最多6张）</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                      <img 
                        src={img.startsWith('data:') ? img : `/api${img}`} 
                        alt={`upload-${idx}`} 
                        className="w-full h-full object-cover" 
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </div>
                  ))}
                  {images.length < 6 && (
                    <button
                      type="button"
                      onClick={handleImageSelect}
                      className="w-20 h-20 md:w-24 md:h-24 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 hover:border-orange-400 hover:bg-orange-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-camera-line text-2xl text-gray-400" />
                      <span className="text-xs text-gray-400 mt-1">添加图片</span>
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  <i className="ri-information-line mr-1"></i>
                  支持从网页复制图片粘贴到这里
                </p>
              </div>

              {/* 详情内容 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  详情内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  onPaste={handlePaste}
                  placeholder="详细描述您的服务内容、优势、价格等信息...
                  
支持从网页复制图文一起粘贴，图片会自动下载到本地"
                  rows={10}
                  maxLength={5000}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.content.length}/5000</p>
              </div>
            </div>
          </div>

          {/* 底部操作栏 */}
          <div className="px-5 md:px-8 py-4 bg-gray-50 border-t border-gray-100">
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="px-6 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors text-sm"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 text-sm shadow-lg shadow-orange-200 cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    {uploadProgress || "发布中..."}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-fill"></i>
                    立即发布
                  </span>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
